import { UseFormProps } from 'react-hook-form';
import { record } from 'fp-ts';
import { pipe } from 'fp-ts/function';
import { createZodFormResolver } from '@mews/framework';
import { compactArray } from '@mews/fp';
import { DetailedUser, OverrideComparator, UserProfileResult } from '@mews-commander/data-access';
import { FieldNames } from '../FieldNames';
import { useUserProfilesGet } from '../../queries';
import { getPrivilegesArray, getRolesAndPermissionsOption, hasDifferentRolesAndPermissions } from '../../utils';
import { MULTIPLE_VALUES, UserType } from '../../types';
import { useUserContext } from '../../context';
import { useIsUserProvisioned } from '../../hooks/useIsUserProvisioned';
import { EditUserFormValues, createUserEditSchema } from './schema';

export const useEditUserFormProps = (
    userProfile: UserProfileResult
): UseFormProps<EditUserFormValues> & { isFetchingData: boolean } => {
    const { refetch, isFetching: isFetchingData } = useUserProfilesGet(userProfile.userId);
    const { userType } = useUserContext();
    const requireName = !useIsUserProvisioned(userProfile) && userType !== UserType.Portfolio;

    const resolver = createZodFormResolver<EditUserFormValues>(createUserEditSchema({ requireName }));

    return {
        resolver,
        defaultValues: async () => {
            const { data } = await refetch();

            const fieldsConfig = getOverriddenFields(data?.overrideComparator);

            return {
                [FieldNames.FirstName]: data?.firstName || '',
                [FieldNames.LastName]: data?.lastName || '',
                [FieldNames.AssignedLearningPath]: getAssignedLearningPath(data),
                [FieldNames.Privileges]: getPrivileges(data),
                [FieldNames.ReceiveCustomerMessages]: getReceiveCustomerMessages(data),
                [FieldNames.Reset2FA]: getReset2FA(data),
                [FieldNames.RoleIds]: getRoleIds(data),
                [FieldNames.RolesAndPermissions]: hasDifferentRolesAndPermissions(fieldsConfig)
                    ? undefined
                    : getRolesAndPermissionsOption(data || ({} as DetailedUser)),
                [FieldNames.SuperiorProfileId]: data?.superiorProfileIds?.[0] || '',
                [FieldNames.DepartmentId]: data?.departments?.[0]?.id || '',
                [FieldNames.Cashiers]: getCashierIds(data),
                [FieldNames.EmployeeIncentiveCode]: getEmployeeIncentiveCodes(data),
            };
        },
        isFetchingData,
    };
};

export const getEmployeeIncentiveCodes = (data: DetailedUser | undefined) => {
    const filteredCodes = compactArray(data?.employeeIncentiveCodes || []);

    if (!data || filteredCodes.length === 0) {
        return '';
    }

    return data.overrideComparator?.equalEmployeeIncentiveCodes ? filteredCodes[0] : filteredCodes.join(', ');
};

export const getAssignedLearningPath = (data: DetailedUser | undefined) =>
    data?.overrideComparator?.equalLearningPaths ? data.learningPaths[0] : MULTIPLE_VALUES;

export const getPrivileges = (data: DetailedUser | undefined) => {
    if (!data || !data?.privileges) {
        return [];
    }

    return getPrivilegesArray(data.privileges);
};

export const getReceiveCustomerMessages = (data: DetailedUser | undefined) => {
    if (!data?.overrideComparator?.equalOptions) {
        return false;
    }

    return !!data?.options?.receiveCustomerMessages;
};

export const getReset2FA = (data: DetailedUser | undefined) => {
    if (!data?.overrideComparator?.equalOptions) {
        return false;
    }

    return !!data?.options?.resetUsersTwoFactorAuthentication;
};

export const getRoleIds = (data: DetailedUser | undefined) => {
    if (!data || !data.roles) {
        return [];
    }

    return data.roles.flatMap(({ id }) => (id ? [id] : []));
};

export const getCashierIds = (data: DetailedUser | undefined) => {
    if (!data || !data.cashiers) {
        return [];
    }

    return data.cashiers.flatMap(({ id }) => (id ? [id] : []));
};

export const getOverriddenFields = (overrideComparator: OverrideComparator | null = {}) => {
    if (!overrideComparator) {
        return [];
    }

    const { allEqual, ...rest } = overrideComparator;

    if (!rest || allEqual) {
        return [];
    }

    return pipe(
        rest,
        record.filter((value) => value === false),
        record.keys
    ) as (keyof OverrideComparator)[];
};
